import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeDropdowns = () => setOpenDropdown(null);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeAll = () => {
    closeDropdowns();
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 sm:px-6 lg:px-12 py-2.5 flex items-center justify-between">
      {/* Logo & Brand Block */}
      <Link to="/" onClick={closeAll} className="flex items-center gap-2.5 flex-shrink-0">
        <img
          src="/logo1.png"
          alt="Logo"
          className="h-8 w-auto object-contain"
        />
        <div className="flex flex-col">
          <div className="text-sm font-black tracking-tight leading-none pb-0.5 border-b-2 border-tertiary">
            <span className="text-secondary">IZI </span>
            <span className="text-tertiary">CALL CENTER</span>
          </div>
          <span className="text-[5.5px] font-bold tracking-wider text-secondary uppercase pt-0.5 hidden sm:block">
            CONNECTER - ASSISTER - SATISFAIRE
          </span>
        </div>
      </Link>

      {/* Desktop Navigation */}
      <nav className="hidden lg:flex items-center gap-7 text-xs font-semibold text-slate-600">
        
        {/* DROPDOWN 1 : Produit */}
        <div 
          className="relative"
          onMouseEnter={() => setOpenDropdown('produit')}
          onMouseLeave={() => setOpenDropdown(null)}
        >
          <button className="hover:text-slate-900 flex items-center gap-1.5 py-2 transition-colors whitespace-nowrap">
            <span>Produit</span>
            <ChevronDown size={13} className={`transition-transform duration-200 ${openDropdown === 'produit' ? 'rotate-180' : ''}`} />
          </button>

          {openDropdown === 'produit' && (
            <div className="absolute top-full left-0 w-48 bg-white border border-slate-100 rounded-xl shadow-xl py-2 space-y-1">
              <Link
                to="/fonctionnalites"
                onClick={closeAll}
                className="block px-4 py-2 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              >
                Fonctionnalités
              </Link>
              <Link
                to="/solutions"
                onClick={closeAll}
                className="block px-4 py-2 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              >
                Solutions
              </Link>
            </div>
          )}
        </div>

        <Link to="/pricing" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          Tarifs
        </Link>
        <Link to="/ressources" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          Ressources
        </Link>
        <Link to="/faq" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          FAQ
        </Link>
        <Link to="/blog" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          Blog
        </Link>
        <Link to="/a-propos" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          À propos
        </Link>
        <Link to="/carrieres" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          Carrières
        </Link>
        <Link to="/contact" onClick={closeAll} className="hover:text-slate-900 transition-colors py-2 whitespace-nowrap">
          Contact
        </Link>
      </nav>

      {/* Desktop Action Buttons */}
      <div className="hidden lg:flex items-center gap-3">
        <Link to="/login" className="text-xs font-bold text-slate-700 hover:text-slate-900 transition-colors whitespace-nowrap">
          Connexion
        </Link>
        <Link to="/register" className="bg-primary text-white text-xs font-bold px-3.5 py-2 rounded-lg shadow-sm hover:opacity-95 transition-all whitespace-nowrap">
          Essayer gratuitement
        </Link>
      </div>

      {/* Mobile Menu Button */}
      <button 
        onClick={toggleMobileMenu}
        className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors z-50"
        aria-label="Menu"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile & Tablet Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm lg:hidden z-40"
            onClick={closeAll}
          />
          
          {/* Mobile Menu avec z-[9999] et top-full pour s'afficher correctement */}
          <div className="fixed top-full left-0 right-0 h-[calc(100vh-100%)] bg-white lg:hidden z-[9999] overflow-y-auto border-t border-slate-100">
            <nav className="flex flex-col p-6 gap-2 text-sm font-semibold text-slate-600">
              
              {/* Produit avec sous-menu */}
              <div className="border-b border-slate-100 pb-2">
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'produit' ? null : 'produit')}
                  className="flex items-center justify-between w-full py-3 hover:text-slate-900 transition-colors"
                >
                  <span>Produit</span>
                  <ChevronDown size={18} className={`transition-transform duration-200 ${openDropdown === 'produit' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'produit' && (
                  <div className="pl-4 space-y-2">
                    <Link to="/fonctionnalites" onClick={closeAll} className="block py-2 hover:text-slate-900 transition-colors">
                      Fonctionnalités
                    </Link>
                    <Link to="/solutions" onClick={closeAll} className="block py-2 hover:text-slate-900 transition-colors">
                      Solutions
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/pricing" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                Tarifs
              </Link>
              <Link to="/ressources" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                Ressources
              </Link>
              <Link to="/faq" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                FAQ
              </Link>
              <Link to="/blog" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                Blog
              </Link>
              <Link to="/a-propos" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                À propos
              </Link>
              <Link to="/carrieres" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                Carrières
              </Link>
              <Link to="/contact" onClick={closeAll} className="py-3 border-b border-slate-100 hover:text-slate-900 transition-colors">
                Contact
              </Link>

              {/* Mobile Action Buttons */}
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col gap-3">
                <Link 
                  to="/login" 
                  onClick={closeAll}
                  className="text-center text-sm font-bold text-slate-700 hover:text-slate-900 transition-colors py-2"
                >
                  Connexion
                </Link>
                <Link 
                  to="/register" 
                  onClick={closeAll}
                  className="text-center bg-primary text-white text-sm font-bold px-4 py-3 rounded-lg shadow-sm hover:opacity-95 transition-all"
                >
                  Essayer gratuitement
                </Link>
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}